import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Select,
  TimePicker,
  Input,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Divider,
  message,
} from "antd";

import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { saveDoctorAvailability } from './../../Services/adminServices';
import { useMasterData } from "../../Components/Common/commonEvents";
import ReceptionistLayout from "../../Layouts/Receptionist/ReceptionistLayout";


const { Title, Text } = Typography;
const { Option } = Select;

export default function DoctorAvailabilityScreen() {
  const sessionUserData = JSON.parse(
    sessionStorage.getItem("userData") || "{}"
  );

  const [loading, setLoading] = useState(false);
  const [doctorId, setDoctorId] = useState(null);

  const [availabilityList, setAvailabilityList] = useState([
    {
      key: Date.now(),
      DayId: undefined,
      StartTime: null,
      EndTime: null,
      SlotDuration: 15,
      Notes: "",
    },
  ]);

  const { usersByRole, doctorSlots, fetchUsersByRole, fetchDoctorSlots } = useMasterData();

  useEffect(() => {
    if (!sessionUserData?.OrgId) return;

    fetchUsersByRole(sessionUserData.OrgId, 2);
  }, [sessionUserData?.OrgId]);

  useEffect(() => {
    if (!sessionUserData?.OrgId || !doctorId) return;

    fetchDoctorSlots(sessionUserData.OrgId, doctorId);
  }, [sessionUserData?.OrgId, doctorId]);

  const days = [
    { Id: 0, Name: "Select All Days" },
    { Id: 1, Name: "Sunday" },
    { Id: 2, Name: "Monday" },
    { Id: 3, Name: "Tuesday" },
    { Id: 4, Name: "Wednesday" },
    { Id: 5, Name: "Thursday" },
    { Id: 6, Name: "Friday" },
    { Id: 7, Name: "Saturday" },
  ];

  const slotDurations = [5, 10, 15, 20, 30, 45, 60];

  useEffect(() => {
    if (!doctorSlots?.length) return;

    const formattedData = doctorSlots.map((slot) => ({
      key: slot.Id,

      Id: slot.Id,

      DayId: slot.DayId,

      StartTime: slot.StartTime
        ? dayjs(slot.StartTime)
        : null,

      EndTime: slot.EndTime
        ? dayjs(slot.EndTime)
        : null,

      SlotDuration: slot.SlotDuration || 15,

      Notes: slot.Notes || "",
    }));

    setAvailabilityList(formattedData);

  }, [doctorSlots]);

  const addRow = () => {
    setAvailabilityList((prev) => [
      ...prev,
      {
        key: Date.now(),
        DayId: undefined,
        StartTime: null,
        EndTime: null,
        SlotDuration: 15,
        Notes: "",
      },
    ]);
  };

  const removeRow = (key) => {
    setAvailabilityList((prev) =>
      prev.filter((item) => item.key !== key)
    );
  };

  const handleChange = (key, field, value) => {

    // DAY VALIDATION
    if (field === "DayId") {

      // SELECT ALL
      if (value === 0) {

        const allDaysData = days
          .filter((d) => d.Id !== 0)
          .map((day, index) => ({
            key: Date.now() + index,

            DayId: day.Id,

            StartTime: null,

            EndTime: null,

            SlotDuration: 15,

            Notes: "",
          }));

        setAvailabilityList(allDaysData);

        return;
      }

      // CHECK DUPLICATE DAY
      const alreadyExists = availabilityList.some(
        (item) =>
          item.key !== key &&
          item.DayId === value
      );

      if (alreadyExists) {
        Swal.fire({
          icon: "warning",
          title: "Day Already Added",
          text: "If you need changes, please modify the existing row.",
          confirmButtonColor: "#16a34a",
        });

        return;
      }
    }

    setAvailabilityList((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
            ...item,
            [field]: value,
          }
          : item
      )
    );
  };

  const handleSaveAvailability = async () => {
    if (!doctorId) {
      message.error("Please select doctor");
      return;
    }
    console.log(availabilityList)
    const hasInvalid = availabilityList.some(
      (item) =>
        !item.DayId ||
        !item.StartTime ||
        !item.EndTime
    );

    if (hasInvalid) {
      message.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        OrgId: sessionUserData?.OrgId,
        DoctorId: doctorId,
        UserId: sessionUserData?.Id,

        Json: availabilityList.map((item) => ({
          Id: item.Id || 0,

          DayId: item.DayId,

          StartTime: dayjs(item.StartTime).format("HH:mm"),

          EndTime: dayjs(item.EndTime).format("HH:mm"),

          SlotDuration: item.SlotDuration,

          Notes: item.Notes,
        })),
      };

      const response = await saveDoctorAvailability(payload);

      if (
        response?.success &&
        response?.code === 1
      ) {
        message.success(
          "Doctor availability saved successfully"
        );
        fetchDoctorSlots(sessionUserData.OrgId, doctorId);

        // setAvailabilityList([
        //   {
        //     key: Date.now(),
        //     DayId: undefined,
        //     StartTime: null,
        //     EndTime: null,
        //     SlotDuration: 15,
        //     Notes: "",
        //   },
        // ]);
      } else {
        message.error(
          response?.message ||
          "Failed to save availability"
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
        "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ReceptionistLayout>

      <div className="container-fluid">

        {/* HEADER */}
        <Card
          bordered={false}
          className="shadow-sm mb-4"
          style={{
            borderRadius: "24px",
            background:
              "linear-gradient(135deg,#052e16,#166534,#22c55e)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

            <div>
              <Title
                level={2}
                className="mb-1 text-white"
              >
                Doctor Availability
              </Title>

              <Text
                className="text-white"
                style={{ opacity: 0.9 }}
              >
                Manage doctor slots and schedules
              </Text>
            </div>

            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CalendarOutlined
                style={{
                  fontSize: 34,
                  color: "#fff",
                }}
              />
            </div>
          </div>
        </Card>

        {/* FORM */}
        <Card
          bordered={false}
          className="shadow-sm"
          style={{
            borderRadius: "24px",
          }}
        >
          <Row gutter={[20, 20]}>

            {/* DOCTOR */}
            <Col xs={24} md={10}>
              <Text strong>
                <UserOutlined className="me-2 text-success" />
                Select Doctor
              </Text>

              <Select
                placeholder="Choose Doctor"
                size="large"
                className="w-100 mt-2"
                value={doctorId}
                onChange={setDoctorId}
              >
                {(usersByRole || []).map((doc) => (
                  <Option
                    key={doc.Id}
                    value={doc.Id}
                  >
                    {doc.Name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* ADD ROW */}
            <Col
              xs={24}
              md={14}
              className="d-flex align-items-end justify-content-end"
            >
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={addRow}
                style={{
                  background:
                    "linear-gradient(135deg,#16a34a,#22c55e)",
                  border: "none",
                  borderRadius: "12px",
                  height: "48px",
                  paddingInline: "24px",
                }}
              >
                Add Availability
              </Button>
            </Col>
          </Row>

          <Divider />

          {/* ROWS */}
          <Space
            direction="vertical"
            size={20}
            className="w-100"
          >
            {availabilityList.map((item) => (
              <Card
                key={item.key}
                bordered={false}
                className="shadow-sm"
                style={{
                  borderRadius: "20px",
                  background: "#f8fafc",
                }}
              >
                <Row gutter={[16, 16]}>

                  {/* DAY */}
                  <Col xs={24} md={4}>
                    <Text strong>Day</Text>

                    <Select
                      placeholder="Select Day"
                      size="large"
                      className="w-100 mt-2"
                      value={item.DayId}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "DayId",
                          value
                        )
                      }
                    >
                      {days.map((day) => (
                        <Option
                          key={day.Id}
                          value={day.Id}
                          disabled={
                            day.Id !== 0 &&
                            availabilityList.some(
                              (a) =>
                                a.key !== item.key &&
                                a.DayId === day.Id
                            )
                          }
                        >
                          {day.Name}
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* START */}
                  <Col xs={24} md={4}>
                    <Text strong>Start Time</Text>

                    <TimePicker
                      size="large"
                      className="w-100 mt-2"
                      format="HH:mm"
                      minuteStep={15}
                      value={item.StartTime}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "StartTime",
                          value
                        )
                      }
                    />
                  </Col>

                  {/* END */}
                  <Col xs={24} md={4}>
                    <Text strong>End Time</Text>

                    <TimePicker
                      size="large"
                      className="w-100 mt-2"
                      format="HH:mm"
                      minuteStep={15}
                      value={item.EndTime}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "EndTime",
                          value
                        )
                      }
                    />
                  </Col>

                  {/* SLOT */}
                  <Col xs={24} md={4}>
                    <Text strong>Slot Duration</Text>

                    <Select
                      size="large"
                      className="w-100 mt-2"
                      value={item.SlotDuration}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "SlotDuration",
                          value
                        )
                      }
                    >
                      {slotDurations.map((slot) => (
                        <Option
                          key={slot}
                          value={slot}
                        >
                          {slot} mins
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* NOTES */}
                  <Col xs={24} md={6}>
                    <Text strong>Notes</Text>

                    <Input
                      size="large"
                      className="mt-2"
                      placeholder="Morning Shift"
                      value={item.Notes}
                      onChange={(e) =>
                        handleChange(
                          item.key,
                          "Notes",
                          e.target.value
                        )
                      }
                    />
                  </Col>

                  {/* DELETE */}
                  <Col
                    xs={24}
                    md={2}
                    className="d-flex align-items-end"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      size="large"
                      onClick={() =>
                        removeRow(item.key)
                      }
                      disabled={
                        availabilityList.length === 1
                      }
                      style={{
                        borderRadius: "12px",
                        width: "100%",
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>

          {/* SAVE */}
          <div className="text-end mt-4">
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={handleSaveAvailability}
              style={{
                height: "52px",
                borderRadius: "14px",
                paddingInline: "32px",
                border: "none",
                background:
                  "linear-gradient(135deg,#15803d,#22c55e)",
                boxShadow:
                  "0 12px 24px rgba(34,197,94,0.35)",
              }}
            >
              Save Availability
            </Button>
          </div>
        </Card>
      </div>
    </ReceptionistLayout>
  );
}